import { Box, Typography, Divider, Stack, styled } from "@mui/material";
import { ProgressButton } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { fetchExpectedPTLState, fetchPTLState } from "~/redux/actions/ptl";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

const PTLModuleStateCard = styled(Stack)(
  ({ theme: { palette, shadows, spacing } }) => ({
    boxShadow: shadows["3"],
    flexDirection: "column",
    alignItems: "stretch",
    backgroundColor: "white",
    border: `1px solid ${palette.border.main}`,
    padding: spacing(0.65), //5px
    borderRadius: "7px",
    minWidth: "250px",
    boxSizing: "border-box"
  })
);

const WorkstationPTLModulesLayout = styled(Stack)(({ theme: { spacing } }) => ({
  padding: spacing(6), //48px
  borderRadius: "7px",
  zIndex: 1000,
  flexDirection: "row",
  flexWrap: "wrap",
  flexBasis: "250px",
  backgroundColor: "white",
  gap: spacing(12), //96px
  justifyContent: "start"
}));

export const PTLStatus = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const actualWorkstationPTLState = useAppSelector(
    (state) => state.ptl.actualWorkstationPTLState
  );
  const expectedWorkstationPTLState = useAppSelector(
    (state) => state.ptl.expectedWorkstationPTLState
  );
  const { data: workstations = [] } = useGetWorkstationsQuery(undefined, {
    refetchOnMountOrArgChange: true
  });

  return (
    <Box sx={{ ml: 1.25 }}>
      {workstations
        ?.slice()
        .sort((w1, w2) =>
          `${w1.autostoreGridName}${w1.ports[0].portId}`.localeCompare(
            `${w2.autostoreGridName}${w2.ports[0].portId}`
          )
        )
        .map((workstation) => (
          <Box key={workstation.id} sx={{ mt: 1.25 }}>
            <Typography
              style={{ marginRight: 20, fontWeight: 600, fontSize: "1em" }}
            >
              {workstation.deviceId} -{" "}
              {expectedWorkstationPTLState[workstation.id]?.statusMessage ?? ""}
            </Typography>
            <ProgressButton
              onClick={async () => {
                await dispatch(fetchPTLState(workstation));
                await dispatch(fetchExpectedPTLState(workstation));
              }}
            >
              {t("fetch ptl state")}
            </ProgressButton>
            <WorkstationPTLModulesLayout
              aria-label={`PTL module statuses for workstation ${workstation.deviceId}`}
              role="region"
            >
              {actualWorkstationPTLState[workstation.id]?.ptlStates
                .map((ptlState) => ({
                  ptlState,
                  expectedPTLState: expectedWorkstationPTLState[
                    workstation.id
                  ]?.ptlStates.get(ptlState.placement.putToLightModuleId)
                }))
                .map(
                  ({ ptlState: { ptlState, placement }, expectedPTLState }) => (
                    <PTLModuleStateCard
                      key={`workstation-ptl-state-${workstation.id}-${
                        placement.putToLightModuleId ?? ""
                      }`}
                      aria-label={`PTL module ${placement.putToLightModuleId ?? "unknown"} status details`}
                      role="region"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%"
                        }}
                      >
                        <Typography style={{ width: "50%" }}>
                          Light Module Id:
                        </Typography>
                        <Typography style={{ width: "50%" }}>{`${
                          placement.putToLightModuleId ?? ""
                        }`}</Typography>
                      </Box>
                      <Divider
                        style={{
                          marginBottom: 5,
                          marginTop: 5,
                          height: 3
                        }}
                      />
                      <Typography style={{ fontWeight: 600, fontSize: 16 }}>
                        Current State
                      </Typography>
                      <Divider
                        style={{
                          marginBottom: 5,
                          marginTop: 5,
                          height: 3
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%"
                        }}
                      >
                        <Typography style={{ width: "50%" }}>
                          Display Data:
                        </Typography>
                        {ptlState?.originalText && (
                          <Typography style={{ width: "50%" }}>
                            {`${ptlState.originalText}`}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%"
                        }}
                      >
                        <Typography style={{ width: "50%" }}>
                          Pick Id:
                        </Typography>
                        {ptlState?.pickId && (
                          <Typography style={{ width: "50%" }}>
                            {`${ptlState.pickId}`}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          width: "100%"
                        }}
                      >
                        <Typography style={{ width: "50%" }}>
                          Tote Id:
                        </Typography>
                        {ptlState?.toteId && (
                          <Typography style={{ width: "50%" }}>
                            {`${ptlState.toteId}`}
                          </Typography>
                        )}
                      </Box>
                      {expectedPTLState && (
                        <>
                          <Divider
                            style={{
                              marginBottom: 5,
                              marginTop: 5,
                              height: 3
                            }}
                          />
                          <Typography style={{ fontWeight: 600, fontSize: 16 }}>
                            Expected State
                          </Typography>
                          <Divider
                            style={{
                              marginBottom: 5,
                              marginTop: 5,
                              height: 3
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              width: "100%"
                            }}
                          >
                            <Typography style={{ width: "50%" }}>
                              Display Data:
                            </Typography>
                            {expectedPTLState.displayData && (
                              <Typography style={{ width: "50%" }}>
                                {`${expectedPTLState.displayData}`}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              width: "100%"
                            }}
                          >
                            <Typography style={{ width: "50%" }}>
                              Pick Id:
                            </Typography>
                            {expectedPTLState.pickId && (
                              <Typography style={{ width: "50%" }}>
                                {`${expectedPTLState.pickId}`}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              width: "100%"
                            }}
                          >
                            <Typography style={{ width: "50%" }}>
                              Tote Id:
                            </Typography>
                            {expectedPTLState.toteId && (
                              <Typography style={{ width: "50%" }}>
                                {`${expectedPTLState.toteId}`}
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </PTLModuleStateCard>
                  )
                )}
            </WorkstationPTLModulesLayout>
          </Box>
        ))}
    </Box>
  );
};
